/**
 * @file THE RESUPPLY DRONE — the arms dealer's free handgun ammunition, flown
 * in and handed over. THE MODEL ONLY: a position, a heading, four states and a
 * cooldown. It draws nothing, it owns no mesh and it has never heard of a rotor
 * blade.
 *
 * Before this file the only doors ammunition had into the world were the shop
 * (`addAmmo`) and a respawn floor that existed solely so "out of ammo AND out of
 * money" could not become a dead end. That floor is DELETED with this round —
 * see `src/game/weapons.js` — because a resupply that arrives while you are
 * still alive is the real fix, and a reserve that quietly refills on death is
 * the paper over it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO THREE.JS, NO ENGINE, NO GAME
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE import — `streamRng`, because rule 4 says a seeded stream and nothing
 * else. Everything the drone talks to (`bus`, `weapons`, `player`, `interiors`,
 * `heightAt`) is INJECTED and **every one of them may be null**: the
 * `Wallet.js` / `Weapons.js` / `VehicleDamage.js` mould. A drone built with all
 * five null is a legal, fully-stepping system that simply never flies, which is
 * what lets `tests/drone.test.mjs` drive the whole machine in node with duck
 * types, no GPU and no browser.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ `Weapons.js` LEARNS NOTHING ABOUT THIS FILE, AND THAT IS THE DESIGN
 * ─────────────────────────────────────────────────────────────────────────────
 * The whole trigger is a SUBSCRIBER. `'player:ammo'` already carries `reserve`
 * on every change, and `Weapons.js` is its sole publisher, so noticing "the
 * player is low" costs exactly one `bus.on` here and **zero lines there**. A
 * future reader will want to "just add a `lowAmmo` flag" or a `'weapon:dry'`
 * event inside the clip machine. Don't: that is a second gate on one question,
 * living in a module that deliberately does not know who is listening — the
 * same family as the `hurtPlayer(amount, lethal)` bug this project already paid
 * for once. The gun says what it holds; this file decides what that means.
 *
 * The only thing that crosses the other way is `addAmmo(n)`, which already
 * clamps at `reserveMax`, publishes synchronously and — the line that makes
 * this feature work at all — **starts a reload when the clip is empty**. There
 * is no manual trigger in this game, so a delivery to a bone-dry player has to
 * arm the gun itself or they are handed sixty rounds they can never fire.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ A FLYING THING IS NOT A COLLIDER, AND THIS ONE REGISTERS NOTHING
 * ─────────────────────────────────────────────────────────────────────────────
 * `CollisionGrid` is 2D and `heightAt` is one scalar — that is why
 * `docs/islands.md` records that a raised span was only ever expressible OVER
 * WATER. A drone that registered a collider would be an invisible wall at
 * ground level under wherever it happens to be hovering, and the player would
 * walk into a locked door in open street. So: this module asks no collision
 * grid, registers no circle, and reads `heightAt` for ONE purpose only —
 * deciding how high off the ground to hold itself. It flies over everything,
 * including the tallest massing in the city, and that is a rule rather than an
 * optimisation.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE FOUR ANSWERS THE OWNER GAVE (do not re-litigate them)
 * ─────────────────────────────────────────────────────────────────────────────
 * • **"Low" is LESS THAN ONE CLIP.** Not a tabled count — see {@link createDrone}'s
 *   `lowAt`, which defaults to the weapon's own `clipSize`.
 * • **Delivery is a DIRECT CREDIT** when the drone reaches the player. There is
 *   no crate, no pickup and no pickup SYSTEM (this project has never had one),
 *   so there is nothing to leave behind, nothing to walk over and nothing to
 *   despawn on a timer.
 * • **Delivery is GUARANTEED.** It cannot be shot down, blocked or outrun —
 *   hence {@link DRONE}`.speed`, which is derived from the car limiter and not
 *   from taste. It steers at the player's LIVE position every step, so driving
 *   away from it is not a strategy.
 * • **It arrives mid-firefight and while driving.** It DEFERS for exactly two
 *   states, both of which are "the player is not somewhere a drone can reach":
 *   dead, and indoors. See `_canFly`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ RULE 4 — AN IDLE SCAN DRAWS NO DICE
 * ─────────────────────────────────────────────────────────────────────────────
 * Exactly ONE die is drawn per sortie — the approach bearing — and it is drawn
 * at LAUNCH, which is an EVENT. A die rolled on a frame where nothing happened
 * makes the stream depend on how long the player stood still, and every later
 * draw in the `'drone'` stream moves with it. `docs/dialogue.md` records the
 * same rule and the re-roll trap that survived every other assertion; here it
 * is pinned by stepping an idle drone 600 times and proving the stream is
 * exactly where a freshly built one left it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ SOLE PUBLISHER OF `'drone:state'` AND `'drone:delivered'`
 * ─────────────────────────────────────────────────────────────────────────────
 * The `Wallet.js` / `'player:cash'` idiom, and for the `'hud:prompt'` reason:
 * two systems on one channel means the HUD shows whichever spoke last and the
 * disagreement is a flicker nobody can reproduce. ⚠ `'drone:state'` fires on
 * STATE TRANSITIONS ONLY — never per frame. A renderer that wants the drone's
 * position sixty times a second reads the getters; the channel is for the four
 * moments that matter. Arming (`pending` going true) is deliberately SILENT:
 * in every case but a defer, `IDLE → INBOUND` follows on the same step and
 * would double the traffic for nothing.
 *
 * @module engine/systems/Drone
 */

import { streamRng } from '../math/rng.js';

/**
 * The four states, in flight order. A plain byte, not a string, because the
 * renderer branches on it every frame — {@link STATE_NAME} is what a human
 * reads.
 */
export const DRONE_STATE = Object.freeze({ IDLE: 0, INBOUND: 1, DELIVER: 2, OUTBOUND: 3 });

/** Index-aligned with {@link DRONE_STATE}. One array, so the two cannot drift. */
const STATE_NAME = Object.freeze(['IDLE', 'INBOUND', 'DELIVER', 'OUTBOUND']);

/**
 * How fast a yaw slews toward the heading of travel, rad/s. VISUAL ONLY and
 * deliberately NOT in {@link DRONE}: it is not tuning anybody balances, it is
 * the difference between a drone that banks into its turn and one that snaps.
 * ~3 rad/s turns the machine through 180° in a second, which at
 * {@link DRONE}`.speed` is a slower turn than its own flight path takes.
 */
const YAW_SLEW = 3;

/**
 * THE TUNING. Frozen, and every value is DERIVED — a drone whose numbers are
 * vibes is a drone somebody retunes into an invisible wall or a lie.
 */
export const DRONE = Object.freeze({
  /**
   * Metres above the sampled ground in transit. The tallest city massing is
   * well under this, so a straight line from the spawn ring to the player never
   * needs a path — the drone simply flies over the block. Raising it costs
   * arrival time (it has to come back DOWN at `climb`); lowering it puts the
   * machine inside a roof.
   */
  cruiseY: 22,
  /**
   * Metres above ground while delivering. Above head height by a clear margin
   * (a chibi is ~1.3 m, the tallest rig well under 3) so it never reads as
   * standing ON the player, and low enough that at the game's 52° pitch it sits
   * inside the same frame as them rather than up in the sky clutter.
   */
  hoverY: 5.5,
  /**
   * Horizontal speed, m/s. ⚠⚠ **DERIVED, NOT TASTE: IT MUST EXCEED THE FASTEST
   * THING THE PLAYER CAN BE RIDING, OR "GUARANTEED DELIVERY" IS A LIE.** A
   * driving car tops out around **17 m/s** at `DRIVE`'s limiter — the same
   * figure `Gameplay`'s respawn note cites as "a corpse doing 17 m/s" — and 26
   * catches that with ~50% of margin, so the closing rate against a player
   * flat out down a trunk road is still 9 m/s. ⚠ **If the car limiter ever
   * rises, this number moves with it.** A drone that a player can outrun turns
   * a guarantee into a chase nobody asked for.
   */
  speed: 26,
  /**
   * Vertical speed, m/s. `cruiseY - hoverY` = 16.5 m of descent, so the drop
   * takes ~1.8 s — it is descending for most of the last 50 m of the approach
   * rather than arriving overhead and then sinking, which reads as a delivery
   * run instead of a lift.
   */
  climb: 9,
  /**
   * Metres from the player it enters at. The camera sees ~38 m, so at 90 the
   * drone is comfortably outside the frame when it appears and always flies IN
   * rather than popping into existence in front of the player. At `speed` that
   * is a ~3.4 s approach — long enough to notice, short enough not to be a wait.
   */
  spawnDist: 90,
  /**
   * XZ metres at which the rounds are credited. Roughly an arm's length plus
   * the actor's own footprint: close enough that the handover is unambiguous,
   * far enough that a single fixed step at `speed` (0.43 m) cannot skip past it.
   */
  reach: 2.6,
  /**
   * Seconds hovering after the credit. Without it the drone would touch the
   * reach ring and immediately leave, and the resupply would read as a
   * teleport — the ammo bar jumps and nothing on screen explains it. 1.2 s is
   * about the length of the HUD's own repaint being noticed.
   */
  hold: 1.2,
  /**
   * Seconds after a delivery before another can be ordered. This is an
   * ANTI-SPAM BOUND, not an economy: handgun ammunition is FREE by owner
   * decision, so the only thing this protects against is a threshold that
   * chatters (fire two rounds, cross the line, fire two more). One sortie
   * delivers five clips, so under any real fire rate the cooldown is invisible.
   */
  cooldown: 6,
  /**
   * Metres from the player at which the sortie is over and the drone goes IDLE.
   * Comfortably past `spawnDist` so it is well out of frame before it stops
   * existing — nothing in this project may delete VISIBLE geometry (see the
   * open wreck-despawn defect in `docs/vehicles.md`), and a drone that blinks
   * out at 40 m would be exactly that bug again.
   */
  exitDist: 120,
  /**
   * Rotor spin, rad/s. ⚠ **VISUAL ONLY.** Nothing in the simulation reads it;
   * it lives here so the renderer does not invent a second number and so the
   * two cannot drift — the `links.js`/`countryroads.js` mirroring trap paid
   * once, deliberately, in the direction that costs nothing.
   */
  spin: 34,
});

/**
 * @param {any} n
 * @returns {boolean} A real, usable number. The `Wallet.js#usable` guard: a
 *   `NaN` out of a malformed injection must not poison a position.
 */
const usable = (n) => typeof n === 'number' && Number.isFinite(n);

/**
 * @param {any} v
 * @param {number} min
 * @returns {number} `v` as a whole number >= `min`, or `min`.
 */
const whole = (v, min) => (usable(v) && v >= min ? Math.floor(v) : min);

/**
 * The resupply drone: one machine, one payload, one sortie at a time.
 *
 * @param {Object} [o]
 * @param {any} [o.bus] `'drone:state'` on every transition and
 *   `'drone:delivered'` on the credit — this module is the SOLE publisher of
 *   both. It also SUBSCRIBES to `'player:ammo'`, which is the entire trigger.
 *   Null means no emits, no subscription and therefore no trigger; everything
 *   else works, and `forceOrder()` still flies it.
 * @param {any} [o.weapons] `engine/systems/Weapons.js`. Read for
 *   `ammoPerBox` / `clipSize` (the two defaults below) and called for
 *   `addAmmo(n)`. Duck-typed and optional at every point.
 * @param {any} [o.player] For `position` and `dead`. A null player reads as the
 *   world origin and as alive, which is only ever reached in a suite.
 * @param {any} [o.interiors] `engine/systems/Interiors.js`, for `plan` and
 *   `locked`. See `_indoors`.
 * @param {((x: number, z: number) => number)|null} [o.heightAt] Ground height.
 *   Absent reads as sea level, which is correct for a flat test plane and wrong
 *   for nothing that ships.
 * @param {number} [o.seed] The level seed. One dedicated `'drone'` stream.
 * @param {number|null} [o.payload] Rounds per delivery. **Defaults to
 *   `weapons.ammoPerBox`** — the box the weapon is authored to take, so a
 *   delivery is five whole clips with no orphan rounds and there is no second
 *   copy of 60 in this file. ⚠ **A payload of 0 makes the whole system inert**:
 *   nothing arms, nothing launches, nothing is published. That is the correct
 *   behaviour for a drone with no gun to feed (no weapons system injected at
 *   all), and it is checked at both the arm and the launch rather than trusted
 *   at one of them.
 * @param {number|null} [o.lowAt] The reserve threshold. **Defaults to
 *   `weapons.clipSize`.** ⚠ **DERIVED, NOT TABLED.** "Low" is "less than one
 *   magazine" — a sentence about the WEAPON, not a number about the drone — so
 *   a weapon with a different clip gets a different threshold for free and
 *   there is no second copy of 12 anywhere in the project. This repo has been
 *   bitten repeatedly by exactly the other choice: `links.js` mirroring
 *   `countryroads.js`'s joint constants, and `ammoPerBox` 60 sitting beside the
 *   shop row's `amount: 60` with nothing tying them together.
 * @returns {Object}
 */
export function createDrone(o = {}) {
  const {
    bus = null,
    weapons = null,
    player = null,
    interiors = null,
    heightAt = null,
    seed = 0,
    payload = null,
    lowAt = null,
  } = o ?? {};

  /** Rounds handed over per sortie. See the param doc: 0 is inert, on purpose. */
  const load = whole(payload ?? weapons?.ammoPerBox ?? 0, 0);
  /** The reserve below which a sortie is ordered. One clip, derived. */
  const low = whole(lowAt ?? weapons?.clipSize ?? 0, 0);

  /**
   * THE stream. One die per sortie, drawn at launch. Dedicated salt so adding a
   * drone to a level cannot reshuffle any other stage — the `streamRng(seed,
   * 'stage')` discipline from rule 4.
   */
  const rng = streamRng(seed, 'drone');

  // ── the state ───────────────────────────────────────────────────────────────
  /** @type {number} One of {@link DRONE_STATE}. */
  let state = DRONE_STATE.IDLE;
  /** A sortie is owed. Survives a defer — that is the whole indoor answer. */
  let pending = false;
  /** Position. Meaningless while IDLE; the renderer draws nothing then. */
  let x = 0;
  let y = 0;
  let z = 0;
  /** Heading of travel, slewed. VISUAL ONLY — nothing below reads it back. */
  let yaw = 0;
  /**
   * The approach bearing as a UNIT VECTOR FROM the player TO the spawn point.
   * The drone therefore travels along its negation, both inbound and outbound:
   * it flies in over the player and straight out the far side, which is what
   * makes the exit read as a delivery run rather than a retreat.
   */
  let bearX = 1;
  let bearZ = 0;
  /** Seconds left of the {@link DRONE}`.hold` hover. */
  let holdT = 0;
  /** Seconds left of the {@link DRONE}`.cooldown`. */
  let coolT = 0;
  /**
   * Did THIS sortie actually credit anything? A sortie aborted by a defer must
   * not spend the cooldown — the player stepped into a shop, and making them
   * wait six seconds after they come out for a delivery that never happened is
   * a punishment for shopping.
   */
  let delivered = false;
  let disposed = false;

  // ── the counters (for `stats()`, `DebugPanel` and the suites) ──────────────
  /** Sorties that reached the player. Counted even when the pouch was full:
   *  the drone flew and handed over, and `rounds` is where the zero shows. */
  let deliveries = 0;
  /** Rounds ACTUALLY credited, lifetime — `addAmmo`'s return, not the payload. */
  let rounds = 0;

  /** @type {Array<() => void>} */
  const unsubs = [];

  // ── the world, read through guards ─────────────────────────────────────────

  /**
   * @param {number} gx @param {number} gz
   * @returns {number} Ground height, or 0. ⚠ The ONLY use of `heightAt` in this
   *   file, and it decides an ALTITUDE — never a collision. See the header.
   */
  const ground = (gx, gz) => {
    if (typeof heightAt !== 'function') return 0;
    const h = heightAt(gx, gz);
    return usable(h) ? h : 0;
  };

  const px = () => (usable(player?.position?.x) ? player.position.x : 0);
  const pz = () => (usable(player?.position?.z) ? player.position.z : 0);

  /**
   * ⚠ **INTERIORS ARE REAL ROOMS 100 m UNDER THE ISLAND.** A player standing at
   * a shop counter is at `y ≈ -100` with the whole plateau over their head, so
   * a delivery attempted "at the player" would be a drone flying down through
   * solid rock to credit somebody through a ceiling. `plan` is a room the
   * player is IN; `locked` is a door transition already running, and a sortie
   * launched into the middle of a fade would arrive in a world the player is no
   * longer standing in.
   *
   * @returns {boolean} The player is not reachable from the sky.
   */
  const _indoors = () => !!interiors?.plan || !!interiors?.locked;

  /**
   * @returns {boolean} A sortie may fly right now. TWO states defer and no
   *   others: **dead** (there is no crediting a corpse — the reserve would move
   *   under a death splash and the player would never see it) and **indoors**.
   *   ⚠ A firefight, a car, a chase and a wanted level are all explicitly NOT
   *   on this list: the drone arrives mid-fight by owner decision, and that is
   *   the whole point of a resupply.
   */
  const _canFly = () => !player?.dead && !_indoors();

  // ── the channel ────────────────────────────────────────────────────────────

  /**
   * ⚠ TRANSITIONS ONLY. Called from exactly one place, so "never per frame" is
   * a property of the code and not of everybody remembering.
   * @param {number} next
   */
  const _setState = (next) => {
    if (next === state) return;
    state = next;
    bus?.emit?.('drone:state', { state, stateName: STATE_NAME[state], pending, x, y, z });
  };

  // ── flight ─────────────────────────────────────────────────────────────────

  /**
   * Slews {@link yaw} toward a heading. ⚠ The world-yaw convention is the
   * traffic one — right of heading `f` is `(-fz, fx)`, so the yaw of `f` is
   * `atan2(-fz, fx)`. A second convention in this project is a car driving
   * backwards; a drone facing the wrong way is the same bug with rotors.
   * @param {number} fx @param {number} fz Unit heading.
   * @param {number} dt
   */
  const _slew = (fx, fz, dt) => {
    const want = Math.atan2(-fz, fx);
    let d = want - yaw;
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    const step = YAW_SLEW * dt;
    yaw += Math.max(-step, Math.min(step, d));
  };

  /**
   * Moves the machine one step toward a horizontal target and one step toward
   * an altitude. The two axes are INDEPENDENT — horizontal at `speed`, vertical
   * at `climb` — which is what makes the descent overlap the approach instead
   * of following it.
   * @param {number} tx @param {number} tz Horizontal target.
   * @param {number} ty Target altitude.
   * @param {number} dt
   */
  const _flyTo = (tx, tz, ty, dt) => {
    const dx = tx - x;
    const dz = tz - z;
    const d = Math.hypot(dx, dz);
    if (d > 1e-6) {
      const k = Math.min(1, (DRONE.speed * dt) / d);
      x += dx * k;
      z += dz * k;
      _slew(dx / d, dz / d, dt);
    }
    const dy = ty - y;
    const stepY = DRONE.climb * dt;
    y += Math.max(-stepY, Math.min(stepY, dy));
  };

  /** @returns {number} XZ metres between the machine and the player. */
  const _distXZ = () => Math.hypot(px() - x, pz() - z);

  /**
   * THE ONE DIE. Picks an approach bearing, places the machine on the spawn
   * ring at cruise altitude and clears the order.
   *
   * ⚠ Called only from `update`'s IDLE branch, i.e. only on a frame where a
   * sortie really begins. That is what makes "an idle scan draws no dice" true
   * by construction rather than by a guard somebody can move.
   */
  const _launch = () => {
    const a = rng() * Math.PI * 2;
    bearX = Math.cos(a);
    bearZ = Math.sin(a);
    x = px() + bearX * DRONE.spawnDist;
    z = pz() + bearZ * DRONE.spawnDist;
    y = ground(x, z) + DRONE.cruiseY;
    // It arrives already pointed at the player — there is nothing on screen for
    // it to have slewed FROM, and a drone that spends its first second turning
    // round looks like it entered backwards.
    yaw = Math.atan2(bearZ, -bearX);
    holdT = 0;
    delivered = false;
    pending = false;
    _setState(DRONE_STATE.INBOUND);
  };

  /**
   * THE HANDOVER. Once per sortie, on ENTRY to DELIVER.
   *
   * ⚠ `addAmmo` is what makes this feature work at all: it clamps at
   * `reserveMax`, publishes `'player:ammo'` synchronously, and **starts a
   * reload when the clip is empty**. There is no manual trigger in this game,
   * so a delivery to a bone-dry player has to arm the gun itself or they are
   * handed five clips and no key that could ever consume them.
   *
   * A pouch that was already full returns 0. That is a real delivery with an
   * empty payload, not a failure: the drone flew, and `rounds` is where the
   * zero is visible.
   */
  const _deliver = () => {
    const got = weapons?.addAmmo?.(load) ?? 0;
    deliveries++;
    rounds += usable(got) ? got : 0;
    delivered = true;
    holdT = DRONE.hold;
    _setState(DRONE_STATE.DELIVER);
    // AFTER the transition, so a subscriber that repaints on `'drone:state'`
    // has already been told the machine is hovering before it is told why.
    bus?.emit?.('drone:delivered', { rounds: usable(got) ? got : 0, x, z });
  };

  /**
   * The sortie is off. Re-arms `pending` when nothing was credited, so a
   * delivery interrupted by a door or a death is RETRIED rather than lost.
   * @param {boolean} rearm
   */
  const _abort = (rearm) => {
    if (rearm) pending = true;
    _setState(DRONE_STATE.OUTBOUND);
  };

  const api = {
    name: 'drone',

    // ── the clock ────────────────────────────────────────────────────────────

    /**
     * One fixed step. ⚠ CALL IT UNCONDITIONALLY — the `Weapons.js` rule: the
     * cooldown is a CLOCK, not an action, and a clock parked behind "may the
     * drone fly" stops in a car, in a shop and under a cutscene and comes back
     * a minute later still at 5.5 s remaining.
     *
     * (An open shop panel is NOT one of the cases this covers: the panel sets
     * the time scale to 0, so `Level#update` is not called at all and the whole
     * sim is frozen, correctly. The drone is deferred indoors anyway.)
     *
     * @param {number} dt Seconds.
     */
    update(dt) {
      if (disposed || !usable(dt) || dt <= 0) return;
      if (coolT > 0) coolT = Math.max(0, coolT - dt);

      switch (state) {
        case DRONE_STATE.IDLE: {
          if (pending && load > 0 && coolT <= 0 && _canFly()) _launch();
          break;
        }

        case DRONE_STATE.INBOUND: {
          // ⚠ The player died or walked through a door mid-flight. Do NOT
          // credit into a corpse or through 100 m of rock: turn round, keep the
          // order, and the delivery lands the moment they are back.
          if (!_canFly()) {
            _abort(true);
            break;
          }
          _flyTo(px(), pz(), ground(x, z) + DRONE.hoverY, dt);
          // Steering at the player's LIVE position every step is what makes the
          // delivery guaranteed — driving away is not a strategy at 26 vs 17.
          if (_distXZ() < DRONE.reach) _deliver();
          break;
        }

        case DRONE_STATE.DELIVER: {
          // Station-keeping. The credit has already happened, so a death or a
          // door here costs nothing and is not worth a branch — the hover just
          // finishes and the machine leaves.
          _flyTo(x, z, ground(x, z) + DRONE.hoverY, dt);
          holdT -= dt;
          if (holdT <= 0) _abort(false);
          break;
        }

        case DRONE_STATE.OUTBOUND: {
          // Straight on along the inbound heading, climbing back to cruise.
          x -= bearX * DRONE.speed * dt;
          z -= bearZ * DRONE.speed * dt;
          _slew(-bearX, -bearZ, dt);
          const ty = ground(x, z) + DRONE.cruiseY;
          const dy = ty - y;
          const stepY = DRONE.climb * dt;
          y += Math.max(-stepY, Math.min(stepY, dy));
          if (_distXZ() > DRONE.exitDist) {
            // ⚠ The cooldown is spent by a DELIVERY, never by a sortie the
            // world aborted — see `delivered`.
            if (delivered) coolT = DRONE.cooldown;
            _setState(DRONE_STATE.IDLE);
          }
          break;
        }

        default:
          break;
      }
    },

    // ── the review-tool door ─────────────────────────────────────────────────

    /**
     * Orders a sortie now, whatever the reserve says.
     *
     * ⚠ **REVIEW-TOOL SURFACE.** Nothing in the shipped game calls this — the
     * only trigger that ships is the `'player:ammo'` subscriber below, because
     * the resupply is AUTOMATIC by owner decision (no key, no prompt, no menu;
     * this game is going to a touchscreen and the whole combat design is
     * auto-combat). It exists so a dev scene can fly the machine on demand
     * instead of draining a reserve first.
     *
     * It bypasses the RESERVE TEST and nothing else: the cooldown, the payload
     * guard and both defers still apply, so a forced sortie flies exactly the
     * flight a real one would.
     *
     * @returns {boolean} An order is now outstanding.
     */
    forceOrder() {
      if (disposed) return false;
      pending = true;
      return true;
    },

    // ── readouts ─────────────────────────────────────────────────────────────

    /** @returns {number} World X. Meaningless while IDLE. */
    get x() {
      return x;
    },
    /** @returns {number} World Y — ALTITUDE, sampled ground plus a constant. */
    get y() {
      return y;
    },
    /** @returns {number} World Z. Meaningless while IDLE. */
    get z() {
      return z;
    },
    /** @returns {number} Heading of travel, radians. VISUAL ONLY. */
    get yaw() {
      return yaw;
    },
    /** @returns {number} One of {@link DRONE_STATE}. */
    get state() {
      return state;
    },
    /** @returns {string} The same thing, for a human. */
    get stateName() {
      return STATE_NAME[state] ?? 'IDLE';
    },
    /** @returns {boolean} There is a machine in the sky — the renderer's gate. */
    get active() {
      return state !== DRONE_STATE.IDLE;
    },
    /** @returns {boolean} A sortie is owed but has not launched (a defer, or a
     *  cooldown). It SURVIVES both, which is why an indoor player still gets
     *  their delivery when they step back onto the pavement. */
    get pending() {
      return pending;
    },

    /**
     * @returns {{state: number, stateName: string, pending: boolean,
     *   deliveries: number, rounds: number, cooldown: number, x: number,
     *   y: number, z: number}} A plain object for `Gameplay.stats()`, the debug
     *   panel and the suites.
     */
    stats() {
      return {
        state,
        stateName: STATE_NAME[state] ?? 'IDLE',
        pending,
        deliveries,
        rounds,
        cooldown: coolT,
        x,
        y,
        z,
      };
    },

    /**
     * Idempotent. Drops the subscription and freezes everything: `update` is a
     * no-op afterwards, an ammo event cannot arm a torn-down drone and nothing
     * more is published, so a system disposed mid-frame cannot repaint a HUD on
     * its way out. The `Wallet#dispose` rule.
     */
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const off of unsubs) {
        try {
          off();
        } catch {
          /* a bus that tore itself down first is not our problem */
        }
      }
      unsubs.length = 0;
    },
  };

  // ── THE TRIGGER ─────────────────────────────────────────────────────────────
  // The entire "notice that the player is low" mechanism, in one subscriber, and
  // `Weapons.js` does not know it exists. See the header before adding a flag
  // over there.
  if (bus?.on) {
    /** @param {any} p A `'player:ammo'` payload — the WHOLE ammo state. */
    const onAmmo = (p) => {
      if (disposed || load <= 0 || low <= 0) return;
      const r = p?.reserve;
      if (!usable(r) || r >= low) return;
      // Each of the three below is a reason NOT to order, and each is checked
      // again in `update`'s IDLE branch — the state and the cooldown can both
      // move between an event and the next step.
      if (pending) return; // idempotent: already ordered, changes nothing, draws nothing
      if (state !== DRONE_STATE.IDLE) return; // one sortie at a time
      if (coolT > 0) return;
      pending = true;
      // ⚠ SILENT. `pending` is not a state, and `IDLE -> INBOUND` lands on the
      // very next step in every case but a defer. See the header.
      //
      // ⚠ THE ONE CORNER: a payload SMALLER than the threshold would leave the
      // player still low after a delivery, and the `'player:ammo'` that
      // `addAmmo` publishes arrives while the state is DELIVER — so it is
      // dropped here and no later event exists to re-order from. Unreachable as
      // shipped (`ammoPerBox` 60 >= `clipSize` 12, five clips against one), and
      // recorded rather than guarded because the guard would be a poll of
      // `weapons.reserve` — a second reader of a number this file has
      // deliberately never held.
    };
    // `createBus().on` returns its own unsubscriber; the `off(type, fn)`
    // fallback is for a duck-typed bus in a suite that does not.
    const offAmmo = bus.on('player:ammo', onAmmo);
    unsubs.push(typeof offAmmo === 'function' ? offAmmo : () => bus.off?.('player:ammo', onAmmo));
  }

  return api;
}
